import { DATE_FORMATS, DateTime } from "@bps/date-time";
import {
  Link,
  ScrollablePane,
  ScrollbarVisibility,
  Stack,
  Text,
  TextBadge,
  TextBadgeColor,
  TextBadgeSize,
  TooltipHost,
  useTheme
} from "@bps/fluent-ui";
import { HttpError } from "@bps/http-client";
import { CopyToClipboardButton } from "@components/buttons/CopyToClipboardButton";
import { DateTimeWithDurationSinceTooltip } from "@components/DateTimeWithDurationSinceTooltip";
import { DetailsRowExpand } from "@components/DetailsRowExpand";
import { DurationText } from "@components/DurationText";
import { DataTable, DataTableColumn } from "@components/tables/DataTable";
import { ScrollListener } from "@components/tables/ScrollListener";
import { FieldDeviceCommandResponse } from "@libs/api/gateways/field/field-ops-gateway.dtos";
import { getDurationSinceNowHumanized } from "@libs/common/common.utils";
import { config } from "@libs/config/config";

interface CommandAuditTableProps {
  items: FieldDeviceCommandResponse[];
  error?: HttpError;
  isLoading?: boolean;
  hasNextPage: boolean | undefined;
  handleScrolledToBottom: () => Promise<void>;
}

const FIELD_DEVICE_LINK_PATH = "/field-mgmt";
const COMMAND_NOT_YET_EXECUTED_TEXT = "Command has not yet executed.";

export const CommandAuditTable: React.FC<CommandAuditTableProps> = ({
  items,
  error,
  isLoading,
  hasNextPage,
  handleScrolledToBottom
}) => {
  const theme = useTheme();

  const getCommandExecutionDurationMs = (
    commandResponse: FieldDeviceCommandResponse
  ): number | undefined => {
    if (
      !commandResponse.commandExecutionUtcStartTime?.isValid ||
      !commandResponse.commandExecutionUtcEndTime?.isValid
    ) {
      return undefined;
    }

    return Math.max(
      commandResponse.commandExecutionUtcEndTime.diff(
        commandResponse.commandExecutionUtcStartTime
      ).milliseconds,
      0
    );
  };

  const renderExpandedDetails = (
    commandResponse?: FieldDeviceCommandResponse
  ): React.ReactNode => {
    if (!commandResponse) {
      return <Text>No details available.</Text>;
    }

    const hasValidCommandExecutionStartTime =
      commandResponse.commandExecutionUtcStartTime?.isValid === true;

    const hasValidCommandExecutionEndTime =
      commandResponse.commandExecutionUtcEndTime?.isValid === true;

    return (
      <Stack tokens={{ childrenGap: theme.spacing.s1 }}>
        <Text>
          <b>ResponsePayloadSize:</b>{" "}
          {commandResponse.responsePayloadSize ?? "N/A"}
        </Text>
        <Text>
          <b>ResponseCode:</b> {commandResponse.responseCode ?? "N/A"}
        </Text>
        <Stack horizontal tokens={{ childrenGap: theme.spacing.s2 }}>
          <Text>
            <b>CreatedDateTime:</b>
          </Text>
          {commandResponse.createdDateTime ? (
            <DateTimeWithDurationSinceTooltip
              dateUtc={commandResponse.createdDateTime}
            />
          ) : (
            <Text>N/A</Text>
          )}
        </Stack>
        <Stack horizontal tokens={{ childrenGap: theme.spacing.s2 }}>
          <Text>
            <b>CommandExecutionUtcStartTime:</b>
          </Text>
          {hasValidCommandExecutionStartTime ? (
            <DateTimeWithDurationSinceTooltip
              dateUtc={
                commandResponse.commandExecutionUtcStartTime ?? undefined
              }
            />
          ) : (
            <Text>{COMMAND_NOT_YET_EXECUTED_TEXT}</Text>
          )}
        </Stack>
        <Stack horizontal tokens={{ childrenGap: theme.spacing.s2 }}>
          <Text>
            <b>CommandExecutionUtcEndTime:</b>
          </Text>
          {hasValidCommandExecutionEndTime ? (
            <DateTimeWithDurationSinceTooltip
              dateUtc={commandResponse.commandExecutionUtcEndTime ?? undefined}
            />
          ) : (
            <Text>{COMMAND_NOT_YET_EXECUTED_TEXT}</Text>
          )}
        </Stack>
      </Stack>
    );
  };

  const columns: DataTableColumn<FieldDeviceCommandResponse>[] = [
    {
      key: "fieldDeviceId",
      name: "FieldDeviceId",
      minWidth: 270,
      maxWidth: 270,
      isResizable: true,
      onRender: (item: FieldDeviceCommandResponse) => (
        <Link
          href={`${FIELD_DEVICE_LINK_PATH}/${item.fieldDeviceId}/fd-overview`}
          target="_blank"
        >
          {item.fieldDeviceId}
        </Link>
      )
    },
    {
      key: "sourceType",
      name: "SourceType",
      minWidth: 120,
      maxWidth: 130,
      isResizable: true,
      onRender: (item: FieldDeviceCommandResponse) => {
        const sourceTypeColorMap: Record<string, TextBadgeColor> = {
          Realtime: TextBadgeColor.green,
          Queued: TextBadgeColor.yellow,
          DeviceInitiated: TextBadgeColor.blue
        };

        const color =
          sourceTypeColorMap[item.sourceType] ?? TextBadgeColor.lightGrey;

        return (
          <TextBadge badgeColor={color} badgeSize={TextBadgeSize.small}>
            {item.sourceType}
          </TextBadge>
        );
      }
    },
    {
      key: "softwarePackageCode",
      name: "SoftwarePackageCode",
      minWidth: 170,
      maxWidth: 220,
      isResizable: true,
      onRender: (item: FieldDeviceCommandResponse) => item.softwarePackageCode
    },
    {
      key: "commandName",
      name: "CommandName",
      minWidth: 170,
      maxWidth: 220,
      isResizable: true,
      onRender: (item: FieldDeviceCommandResponse) => item.commandName
    },
    {
      key: "responseCode",
      name: "Result",
      minWidth: 110,
      maxWidth: 110,
      isResizable: true,
      onRender: (item: FieldDeviceCommandResponse) => {
        if (item.responseCode == null) {
          return (
            <TextBadge
              badgeColor={TextBadgeColor.lightGrey}
              badgeSize={TextBadgeSize.small}
            >
              N/A
            </TextBadge>
          );
        }

        const isSuccess = item.responseCode >= 200 && item.responseCode < 300;

        return (
          <TextBadge
            badgeColor={isSuccess ? TextBadgeColor.green : TextBadgeColor.red}
            badgeSize={TextBadgeSize.small}
          >
            {isSuccess ? "Success" : "Unsuccessful"}
          </TextBadge>
        );
      }
    },
    {
      key: "speed",
      name: "Speed",
      minWidth: 70,
      maxWidth: 70,
      isResizable: true,
      onRender: (item: FieldDeviceCommandResponse) => {
        const commandExecutionDurationMs = getCommandExecutionDurationMs(item);

        if (commandExecutionDurationMs == null) {
          return (
            <TextBadge
              badgeColor={TextBadgeColor.lightGrey}
              badgeSize={TextBadgeSize.small}
            >
              N/A
            </TextBadge>
          );
        }

        return <DurationText duration={commandExecutionDurationMs} />;
      }
    },
    {
      key: "correlationId",
      name: "CorrelationId",
      minWidth: 310,
      maxWidth: 310,
      isResizable: true,
      onRender: (item: FieldDeviceCommandResponse) => {
        if (!item.correlationId) {
          return null;
        }

        const seqLink = config.seqUrl ? (
          <Link
            href={`${config.seqUrl}?range=1d&filter="${item.correlationId}"`}
            target="_blank"
          >
            {item.correlationId}
          </Link>
        ) : (
          <Text>{item.correlationId}</Text>
        );

        return (
          <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 4 }}>
            {seqLink}
            <CopyToClipboardButton clipboardContent={item.correlationId} />
          </Stack>
        );
      }
    },
    {
      key: "createdDateTime",
      name: "CreatedDateTime",
      minWidth: 140,
      maxWidth: 170,
      isResizable: true,
      onRender: (item: FieldDeviceCommandResponse) => {
        if (!item.createdDateTime) {
          return null;
        }

        const relativeTime = `${getDurationSinceNowHumanized(
          item.createdDateTime
        )} ago`;

        const formattedDate = (item.createdDateTime as DateTime).toFormat(
          DATE_FORMATS.LONG_DATE_FORMAT_MONTH_AHEAD_CUT
        );

        return (
          <TooltipHost content={formattedDate}>{relativeTime}</TooltipHost>
        );
      }
    }
  ];

  return (
    <Stack grow styles={{ root: { minHeight: 420, position: "relative" } }}>
      <Stack styles={{ root: { paddingBottom: theme.spacing.s1 } }}>
        <Text variant="small">
          Click a row to view command execution details.
        </Text>
      </Stack>
      <ScrollablePane scrollbarVisibility={ScrollbarVisibility.auto}>
        <DataTable
          stickyHeader
          enableShimmer={isLoading}
          errorMessage={error?.message}
          isSelectedOnFocus
          columns={columns}
          items={items}
          onRenderRow={row => {
            if (!row) {
              return null;
            }

            return (
              <DetailsRowExpand
                row={row}
                expand={
                  <Stack
                    tokens={{ childrenGap: theme.spacing.s1 }}
                    styles={{
                      root: {
                        padding: theme.spacing.s1,
                        background: theme.semanticColors.bodyBackground
                      }
                    }}
                  >
                    {renderExpandedDetails(row.item)}
                  </Stack>
                }
              />
            );
          }}
        />
        <ScrollListener
          hasNextPage={hasNextPage ?? false}
          onScrolledToBottom={handleScrolledToBottom}
        />
      </ScrollablePane>
    </Stack>
  );
};
